import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Divider,
  TextField,
  Checkbox,
  FormControlLabel,
  Link,
  FormHelperText,
  Button,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CloseIcon from "@mui/icons-material/Close";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";

import PaymentForm from "./components/PaymentForm";

dayjs.extend(utc);
dayjs.extend(timezone);

// Simple money formatter (fallback)
const formatMoney = (n = 0) => `$${Number(n || 0).toFixed(2)}`;

const Checkout = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const location = useLocation();
  const [cartId, setCartId] = useState(null);
  const [discountCode, setDiscountCode] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { loadStripe } = await import("@stripe/stripe-js");
        const sp = await loadStripe(process.env.REACT_APP_STRIPE_KEY);
        if (mounted) setStripePromise(sp);
      } catch (e) {
        // no-op; PaymentForm will handle absence
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);
  const [eventTitle, setEventTitle] = useState(
    location.state?.title || "Event title"
  );
  const [eventWhen, setEventWhen] = useState(
    location.state?.whenText || "Event date • time"
  );
  const [eventImage, setEventImage] = useState(null);
  const [termsUrl, setTermsUrl] = useState("");
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [assignees, setAssignees] = useState([]); // [{ lineIndex, unitIndex, ticketTypeId, ticketTypeName, firstName, lastName, email }]
  const [cartTotals, setCartTotals] = useState({
    subtotal: 0,
    fees: 0,
    total: 0,
  });
  const isFreeCart = useMemo(
    () => Number(cartTotals?.total || 0) <= 0,
    [cartTotals?.total]
  );
  const [cartExpiresAt, setCartExpiresAt] = useState(
    location.state?.cartExpiresAt || null
  );
  const [agreeTOS, setAgreeTOS] = useState(false);
  const [tosAttempt, setTosAttempt] = useState(false); // optional: future submit validation
  const [pi, setPi] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(true); // assume auth by default; adjusted on hydrate

  // Basic hydration – safe to remove/replace with your request() helper
  useEffect(() => {
    getData();
  }, [eventId]);

  useEffect(() => {
    if (cartId && !pi && !isFreeCart) {
      createPI();
    }
  }, [cartId, pi, isFreeCart]);

  const getData = async () => {
    try {
      const res = await request(`ticketing/checkout/?eventId=${eventId}`);
      if (res?.success && res.data) {
        const c = res.data?.cart;
        setCartId(c._id);
        setDiscountCode(c.discountCode);
        const p = c.pricing || {};
        setCartTotals({
          subtotal:
            p.subtotalCents != null ? p.subtotalCents / 100 : p.subtotal || 0,
          fees: p.feesCents != null ? p.feesCents / 100 : p.fees || 0,
          total: p.totalCents != null ? p.totalCents / 100 : p.total || 0,
        });
        setCartExpiresAt(c.expiresAt || null);
        const event = res.data?.event;
        setEventTitle(event.name);
        if (event.start) {
          setEventWhen(event.start);
        } else {
          setEventWhen(event.date);
        }
        setEventImage(event.imageUrl);
        setTermsUrl(res?.data?.terms);
        setProfile(res.data?.profile);
        // Infer auth state: if server gives us a user/profile id, consider authenticated
        const prof = res.data?.profile;
        setIsAuthenticated(
          Boolean(prof && (prof._id || prof.id || prof.userId))
        );
        // Build per-ticket assignee boxes for multi-ticket purchases
        const cart = res.data?.cart;
        const prof2 = res.data?.profile || {};
        const newAssignees = [];
        if (cart && Array.isArray(cart.items)) {
          cart.items.forEach((it, lineIndex) => {
            const qty = Number(it.quantity || 0);
            const typeName = it.ticketType?.name || "Ticket";
            const typeId = it.ticketType?._id || it.ticketType; // supports id or populated object
            for (let unitIndex = 0; unitIndex < qty; unitIndex++) {
              newAssignees.push({
                lineIndex,
                unitIndex,
                ticketTypeId: typeId,
                ticketTypeName: typeName,
                firstName: "",
                lastName: "",
                email: "",
                sameAsBilling: false,
              });
            }
          });
        }
        setAssignees(newAssignees);
      }
    } catch (_) {
      /* noop – leave placeholders */
    }
  };

  const createPI = async () => {
    const result = await request(
      `ticketing/paymentintent/${cartId}`,
      "post",
      {}
    );
    if (result.success) {
      setPi(result.data);
    } else {
      toast.error(result.error);
    }
  };

  // ---- Countdown (same pattern as ticketing) ----
  const [remainingMs, setRemainingMs] = useState(null);
  const countdownText = useMemo(() => {
    if (remainingMs == null) return null;
    const total = Math.max(0, Math.floor(remainingMs / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }, [remainingMs]);

  useEffect(() => {
    let timer;
    const update = () => {
      if (!cartExpiresAt) {
        setRemainingMs(null);
        return;
      }
      const diff = new Date(cartExpiresAt).getTime() - Date.now();
      setRemainingMs(diff > 0 ? diff : 0);
    };
    update();
    if (cartExpiresAt) timer = setInterval(update, 1000);
    return () => timer && clearInterval(timer);
  }, [cartExpiresAt]);

  // Compute duplicate assignee emails (case-insensitive, trimmed). Empty allowed.
  const billingEmail = useMemo(
    () => (profile?.email || "").trim().toLowerCase(),
    [profile?.email]
  );
  const duplicateEmailSet = useMemo(() => {
    const counts = new Map();
    (assignees || []).forEach((a) => {
      const em = (a?.sameAsBilling ? billingEmail : a?.email || "")
        .trim()
        .toLowerCase();
      if (!em) return; // blanks are allowed
      counts.set(em, (counts.get(em) || 0) + 1);
    });
    const dups = new Set();
    counts.forEach((count, em) => {
      if (count > 1) dups.add(em);
    });
    return dups;
  }, [assignees, billingEmail]);

  const hasDuplicateAssigneeEmails = duplicateEmailSet.size > 0;

  const paymentDisabled = !agreeTOS || hasDuplicateAssigneeEmails;

  const onBackToTickets = () => navigate(`/tickets/${eventId}`);
  const onCloseAll = () => navigate(`/events`);

  const updateAssignee = (idx, field, value) => {
    setAssignees((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const updateProfileField = (field, value) => {
    if (isAuthenticated) return; // in authenticated mode, profile edits happen in the account page
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSameAsBilling = (idx, checked) => {
    setAssignees((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], sameAsBilling: checked };
      return next;
    });
  };

  const saveAllocationsToCart = async () => {
    // Block save/confirm if duplicate recipient emails present
    if (hasDuplicateAssigneeEmails) {
      toast.error("Each ticket must have a unique recipient email.");
      return false;
    }
    try {
      // Group assignees by lineIndex
      const byLine = new Map();
      assignees.forEach((a) => {
        if (!byLine.has(a.lineIndex)) byLine.set(a.lineIndex, []);
        byLine.get(a.lineIndex).push(a);
      });
      // Build items payload using current cart structure from last fetch
      const res = await request(`ticketing/checkout/?eventId=${eventId}`);
      if (!res?.success || !res.data?.cart) return true; // nothing to do
      const cart = res.data.cart;
      const itemsPayload = (cart.items || []).map((it, lineIndex) => {
        const lineAssignees = (byLine.get(lineIndex) || []).sort(
          (a, b) => a.unitIndex - b.unitIndex
        );
        const allocations = lineAssignees.map((a, idx) => {
          const fn = a.sameAsBilling
            ? profile?.firstName || ""
            : a.firstName || "";
          const ln = a.sameAsBilling
            ? profile?.lastName || ""
            : a.lastName || "";
          const em = a.sameAsBilling ? profile?.email || "" : a.email || "";
          const t =
            a.sameAsBilling ||
            (em && em === (profile?.email || "") && idx === 0)
              ? "purchaser"
              : "guest";
          const entry = { type: t };
          if (fn || ln) entry.name = `${fn}${ln ? " " + ln : ""}`.trim();
          if (em) entry.email = em.trim().toLowerCase();
          return entry;
        });
        return {
          ticketTypeId: it.ticketType?._id || it.ticketType,
          quantity: Number(it.quantity || 0),
          allocations,
        };
      });
      // Call upsert to persist allocations (server will normalize)
      const u = await request("ticketing/cart/upsert", "post", {
        eventId,
        items: itemsPayload,
        discountCode,
      });
      if (!u?.success) {
        toast.error(u?.error || "Unable to save ticket recipients");
        return false;
      }
      return true;
    } catch (e) {
      toast.error(e?.message || "Unable to save ticket recipients");
      return false;
    }
  };

  const handleCompleteFreeOrder = async () => {
    // validate & persist allocations first
    const ok = await saveAllocationsToCart();
    if (!ok) return;

    try {
      const res = await request("ticketing/checkout/free", "post", {
        cartId
      });
      if (res?.success) {
        const orderId = res?.orderId;
        navigate(`/checkout/success/${eventId}/${orderId}`);
      } else {
        toast.error(res?.error || "Unable to complete registration.");
      }
    } catch (e) {
      toast.error(e?.message || "Unable to complete registration.");
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Top bar */}
      <Box sx={{ px: 1, py: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <IconButton onClick={onBackToTickets} aria-label="Back to tickets">
            <ArrowBackIosNewIcon />
          </IconButton>
          <Stack
            alignItems="center"
            spacing={0}
            sx={{ minHeight: 40, flex: 1 }}
          >
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              Checkout
            </Typography>
            <Typography
              variant="body2"
              color={
                remainingMs !== null && remainingMs <= 60000
                  ? "error"
                  : "text.secondary"
              }
            >
              {countdownText ? `Time left ${countdownText}` : " "}
            </Typography>
          </Stack>
          <IconButton onClick={onCloseAll} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Divider />

      {eventImage ? (
        <Box sx={{ p: 2 }}>
          <Box
            component="img"
            src={eventImage}
            alt={eventTitle}
            sx={{
              width: { xs: "100%", md: "50%" },
              display: "block",
              mx: "auto",
            }}
          />
        </Box>
      ) : null}

      {/* Event summary row */}
      <Box
        sx={{
          px: 2,
          py: 2,
          width: { sx: "100%", md: "50%" },
          display: "block",
          mx: "auto",
        }}
      >
        <Stack
          direction="column"
          spacing={1}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={{ mb: 0.25 }}>
              {eventTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {dayjs.utc(eventWhen).local().format("MMMM D, YYYY h:mm A")}
            </Typography>
          </Box>
          <Typography variant="h6">{formatMoney(cartTotals.total)}</Typography>
          <Typography>
            Refunds are available up to 7 days before the event. After that, all
            sales are final.
          </Typography>
        </Stack>
      </Box>

      <Divider />

      {/* Billing information (start of form) */}
      <Box
        sx={{
          px: 2,
          py: 2,
          width: { sx: "100%", md: "50%" },
          display: "block",
          mx: "auto",
        }}
      >
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Billing information
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Required<span style={{ color: "#d32f2f" }}>*</span>
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={3}
          sx={{ mb: 2 }}
        >
          <TextField
            label="First name"
            required
            fullWidth
            value={profile.firstName}
            onChange={(e) => updateProfileField("firstName", e.target.value)}
            disabled={isAuthenticated}
            helperText={isAuthenticated ? "Edit in your profile" : ""}
          />
          <TextField
            label="Last name"
            required
            fullWidth
            value={profile?.lastName}
            onChange={(e) => updateProfileField("lastName", e.target.value)}
            disabled={isAuthenticated}
            helperText={isAuthenticated ? "Edit in your profile" : ""}
          />
          <TextField
            label="Email address"
            type="email"
            required
            fullWidth
            value={profile?.email}
            onChange={(e) => updateProfileField("email", e.target.value)}
            disabled={isAuthenticated}
            helperText={isAuthenticated ? "Edit in your profile" : ""}
          />
        </Stack>
        {!isAuthenticated && !profile?.email && (
          <FormHelperText error>
            Please enter your billing email before assigning a ticket to
            yourself.
          </FormHelperText>
        )}
      </Box>

      <Divider />

      {/* Assignee boxes for multiple tickets */}
      {assignees && assignees.length > 0 && (
        <Box
          sx={{
            px: 2,
            py: 2,
            width: { sx: "100%", md: "50%" },
            display: "block",
            mx: "auto",
          }}
        >
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Ticket recipients
          </Typography>
          <Stack spacing={2}>
            {assignees.map((a, idx) => (
              <Box
                key={`${a.lineIndex}-${a.unitIndex}`}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle1">
                    {a.ticketTypeName}
                    {assignees.filter((x) => x.lineIndex === a.lineIndex)
                      .length > 1
                      ? ` — Ticket ${a.unitIndex + 1}`
                      : ""}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!a.sameAsBilling}
                        onChange={(e) =>
                          toggleSameAsBilling(idx, e.target.checked)
                        }
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">Same as billing</Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="First name"
                    fullWidth
                    value={
                      a.sameAsBilling ? profile.firstName || "" : a.firstName
                    }
                    disabled={!!a.sameAsBilling}
                    onChange={(e) =>
                      updateAssignee(idx, "firstName", e.target.value)
                    }
                  />
                  <TextField
                    label="Last name"
                    fullWidth
                    value={
                      a.sameAsBilling ? profile.lastName || "" : a.lastName
                    }
                    disabled={!!a.sameAsBilling}
                    onChange={(e) =>
                      updateAssignee(idx, "lastName", e.target.value)
                    }
                  />
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    value={a.sameAsBilling ? profile.email || "" : a.email}
                    disabled={!!a.sameAsBilling}
                    error={(() => {
                      const resolved = (
                        a.sameAsBilling ? billingEmail : a.email || ""
                      )
                        .trim()
                        .toLowerCase();
                      return !!resolved && duplicateEmailSet.has(resolved);
                    })()}
                    helperText={(() => {
                      const resolved = (
                        a.sameAsBilling ? billingEmail : a.email || ""
                      )
                        .trim()
                        .toLowerCase();
                      return !!resolved && duplicateEmailSet.has(resolved)
                        ? "Duplicate email — each ticket needs a unique recipient."
                        : "";
                    })()}
                    onChange={(e) =>
                      updateAssignee(
                        idx,
                        "email",
                        e.target.value.toLocaleLowerCase()
                      )
                    }
                  />
                </Stack>
                {(() => {
                  const recipientEmail = a.sameAsBilling
                    ? billingEmail
                    : (a.email || "").trim().toLowerCase();
                  return recipientEmail && recipientEmail !== billingEmail ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      An email will be sent to this person informing them they
                      have a ticket.
                    </Typography>
                  ) : null;
                })()}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <Divider />

      {/* Terms & Conditions clickwrap */}
      <Box
        sx={{
          px: 2,
          py: 2,
          width: { sx: "100%", md: "50%" },
          display: "block",
          mx: "auto",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={agreeTOS}
              onChange={(e) => setAgreeTOS(e.target.checked)}
              inputProps={{ "aria-label": "Agree to terms and conditions" }}
            />
          }
          label={
            <Typography variant="body2">
              I agree to the{" "}
              <Link href={termsUrl} target="_blank" rel="noopener">
                Terms and Conditions
              </Link>
              .
            </Typography>
          }
        />
        {!agreeTOS && tosAttempt && (
          <FormHelperText error sx={{ ml: 1.5 }}>
            You must agree to the Terms and Conditions to continue.
          </FormHelperText>
        )}
      </Box>

      <Divider />

      {/* Payment section */}
      <Box
        sx={{
          px: 2,
          py: 2,
          width: { sx: "100%", md: "50%" },
          display: "block",
          mx: "auto",
        }}
      >
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Payment
        </Typography>
        {isFreeCart ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No payment required for free tickets.
            </Typography>
            {hasDuplicateAssigneeEmails && (
              <FormHelperText error sx={{ mb: 1 }}>
                Resolve duplicate recipient emails before completing
                registration.
              </FormHelperText>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={handleCompleteFreeOrder}
              disabled={!agreeTOS || hasDuplicateAssigneeEmails}
            >
              Complete registration
            </Button>
            <div style={{ height: 100 }} />
          </>
        ) : (
          <>
            {hasDuplicateAssigneeEmails && (
              <FormHelperText error sx={{ mb: 1 }}>
                Resolve duplicate recipient emails to proceed to payment.
              </FormHelperText>
            )}
            {!stripePromise || !pi?.clientSecret ? (
              <Typography variant="body2" color="text.secondary">
                Preparing payment options…
              </Typography>
            ) : null}
            {stripePromise && pi?.clientSecret && (
              <PaymentForm
                stripePromise={stripePromise}
                clientSecret={pi.clientSecret}
                profile={profile}
                disabled={!agreeTOS || hasDuplicateAssigneeEmails}
                onBeforeConfirm={saveAllocationsToCart}
                onSucceeded={(paymentIntent) => {
                  navigate(`/checkout/success/${eventId}/${paymentIntent.id}`);
                }}
                onError={(msg) => toast.error(msg)}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Checkout;
